import React, { useState, Suspense, lazy } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Layout/Navbar';
import Sidebar from './components/Layout/Sidebar';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import authService from './services/authService';
import { ToastProvider } from './contexts/ToastContext';

// OPTIMIZED: Code splitting with React.lazy for better performance
// Eager-loaded components (critical for initial render)
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';

// Lazy-loaded components (loaded on demand)
const Register = lazy(() => import('./components/Auth/Register'));
const UserList = lazy(() => import('./components/Users/UserList'));

// Vehicle Components - Lazy loaded
const VehicleList = lazy(() => import('./components/Vehicles/VehicleList'));
const VehicleDetails = lazy(() => import('./components/Vehicles/VehicleDetails'));
const CreateVehicle = lazy(() => import('./components/Vehicles/CreateVehicle'));
const EditVehicle = lazy(() => import('./components/Vehicles/EditVehicle'));
const AssignTyres = lazy(() => import('./components/Vehicles/AssignTyres'));
const VehicleTrips = lazy(() => import('./components/Vehicles/VehicleTrips'));

// Tyre Components - Lazy loaded
const TyreList = lazy(() => import('./components/Tyres/TyreList'));
const TyreHistory = lazy(() => import('./components/Tyres/TyreHistory'));
const CreateTyre = lazy(() => import('./components/Tyres/CreateTyre'));
const EditTyre = lazy(() => import('./components/Tyres/EditTyre'));

// Trip Components - Lazy loaded
const TripsList = lazy(() => import('./components/Trips/TripsList'));
const CreateTrip = lazy(() => import('./components/Trips/CreateTrip'));
const AllTrips = lazy(() => import('./components/Trips/AllTrips'));

// Resole Components - Lazy loaded
const ResoleDashboard = lazy(() => import('./components/Resoles/ResoleDashboard'));

// Maintenance Components - Lazy loaded
const MaintenanceDashboard = lazy(() => import('./components/Maintenance/MaintenanceDashboard'));
const PeriodicMaintenanceRules = lazy(() => import('./components/PeriodicMaintenance/PeriodicMaintenanceRules'));
const MaintenanceNotifications = lazy(() => import('./components/PeriodicMaintenance/MaintenanceNotifications'));

// Report Components - Lazy loaded
const TyreReport = lazy(() => import('./components/Reports/TyreReport'));

// Approval Components - Lazy loaded
const ApprovalsList = lazy(() => import('./components/Approvals/ApprovalsList'));
const MyApprovalsList = lazy(() => import('./components/Approvals/MyApprovalsList'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);
const Unauthorized = () => (
  <div className="container-padding section-spacing">
    <div className="card-base text-center py-12">
      <h1 className="text-4xl font-bold text-red-500">Unauthorized</h1>
      <p className="mt-4 text-gray-700">You don't have permission to access this page.</p>
    </div>
  </div>
);
const NotFound = () => (
  <div className="container-padding section-spacing">
    <div className="card-base text-center py-12">
      <h1 className="text-4xl font-bold text-gray-900">404 - Page Not Found</h1>
      <p className="mt-4 text-gray-700">The page you're looking for doesn't exist.</p>
    </div>
  </div>
);

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Public routes that should not show sidebar/navbar
  const publicRoutes = ['/login', '/unauthorized'];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const isAuthenticated = authService.isAuthenticated();
  const showLayout = !isPublicRoute && isAuthenticated;

  return (
    <div className="min-h-screen bg-gray-50">
      {showLayout && (
        <>
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          <Navbar toggleSidebar={toggleSidebar} />
        </>
      )}
      <main className={showLayout ? "lg:ml-64" : ""}>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Protected Routes - Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Auth/User Management */}
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredRole="admin">
                <UserList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/register"
            element={
              <ProtectedRoute requiredRole="admin">
                <Register />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Vehicles */}
          <Route
            path="/vehicles"
            element={
              <ProtectedRoute>
                <VehicleList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicles/create"
            element={
              <ProtectedRoute requiredRole="worker">
                <CreateVehicle />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicles/:id"
            element={
              <ProtectedRoute>
                <VehicleDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicles/:id/edit"
            element={
              <ProtectedRoute requiredRole="worker">
                <EditVehicle />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicles/:id/assign-tyres"
            element={
              <ProtectedRoute requiredRole="admin">
                <AssignTyres />
              </ProtectedRoute>
            }
          />

          <Route
            path="/vehicles/:id/trips"
            element={
              <ProtectedRoute>
                <VehicleTrips />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Tyres */}
          <Route
            path="/tyres"
            element={
              <ProtectedRoute>
                <TyreList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tyres/create"
            element={
              <ProtectedRoute requiredRole="worker">
                <CreateTyre />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tyres/:id/history"
            element={
              <ProtectedRoute>
                <TyreHistory />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tyres/:id/edit"
            element={
              <ProtectedRoute requiredRole="worker">
                <EditTyre />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Trips */}
          <Route
            path="/trips/all"
            element={
              <ProtectedRoute>
                <AllTrips />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trips"
            element={
              <ProtectedRoute>
                <TripsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trips/create"
            element={
              <ProtectedRoute requiredRole="worker">
                <TripsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:vehicleId/trips/create"
            element={
              <ProtectedRoute requiredRole="worker">
                <CreateTrip />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Resoles */}
          <Route
            path="/resoles"
            element={
              <ProtectedRoute>
                <ResoleDashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Maintenance */}
          <Route
            path="/maintenance"
            element={
              <ProtectedRoute>
                <MaintenanceDashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Periodic Maintenance */}
          <Route
            path="/maintenance-rules"
            element={
              <ProtectedRoute requiredRole="admin">
                <PeriodicMaintenanceRules />
              </ProtectedRoute>
            }
          />

          <Route
            path="/maintenance-notifications"
            element={
              <ProtectedRoute>
                <MaintenanceNotifications />
              </ProtectedRoute>
            }
          />
n          {/* Protected Routes - Reports */}
          <Route
            path="/reports/tyres"
            element={
              <ProtectedRoute>
                <TyreReport />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Approvals */}
          <Route
            path="/approvals"
            element={
              <ProtectedRoute requiredRole="admin">
                <ApprovalsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-requests"
            element={
              <ProtectedRoute requiredRole="worker">
                <MyApprovalsList />
              </ProtectedRoute>
            }
          />

          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <HashRouter>
        <AppContent />
      </HashRouter>
    </ToastProvider>
  );
}


export default App;

